import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { MinusIcon, PlusIcon } from "@heroicons/react/outline";

import { getData, postData } from "../backend";
import { useModal } from "../contexts";
import useUser from "../hooks/useUser";
import { Team } from "../interfaces";
import Modal from "./Modal";
import ErrorModal from "./ErrorModal";


interface PreviewResponse {
  amountDue: number;
  currency: string;
  currentSeats: number;
  newSeats: number;
}

function formatAmount(amount: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

interface Props {
  initialSeats?: number;
}

function SettingsTeamSeatsModal(props: Props) {
  const { closeModal, openModal } = useModal();
  const { t } = useTranslation();

  const { user, refreshUser } = useUser();
  const { data: team, mutate: revalidateTeam } = useSWR<Team>("/auth/team/", getData);

  const currentSeats = team?.seats ?? 0;
  const memberCount = team?.members?.length ?? 0;
  const minSeats = Math.max(memberCount, 1);

  const [targetSeats, setTargetSeats] = useState<number>(props.initialSeats ?? currentSeats + 1);

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const previewRequestId = useRef(0);
  const previewDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function changeSeats(newSeats: number) {
    setTargetSeats(newSeats);

    if (previewDebounceTimer.current) clearTimeout(previewDebounceTimer.current);

    if (newSeats === currentSeats || newSeats < minSeats) {
      setPreview(null);
      setPreviewError(null);
      setIsPreviewLoading(false);
      return;
    }

    setIsPreviewLoading(true);
    setPreviewError(null);

    previewDebounceTimer.current = setTimeout(async () => {
      const requestId = ++previewRequestId.current;
      try {
        const data: PreviewResponse = await postData("/billing/team-seats/preview/", { seats: newSeats });
        if (requestId === previewRequestId.current) setPreview(data);
      } catch (err: any) {
        if (requestId === previewRequestId.current) setPreviewError(err?.message ?? t("Could not load price preview."));
      } finally {
        if (requestId === previewRequestId.current) setIsPreviewLoading(false);
      }
    }, 300);
  }

  if (!team || !user) return null;

  const isIncrease = targetSeats > currentSeats;
  const isDecrease = targetSeats < currentSeats;
  const isNoChange = targetSeats === currentSeats;

  async function handleConfirm() {
    if (isNoChange || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await postData("/billing/team-seats/", { seats: targetSeats });
      await Promise.all([refreshUser(), revalidateTeam()]);
      closeModal();
    } catch (error) {
      setIsSubmitting(false);
      openModal(<ErrorModal details={error} />);
    }
  }

  function decrement() {
    changeSeats(Math.max(minSeats, targetSeats - 1));
  }

  function increment() {
    changeSeats(targetSeats + 1);
  }

  return (
    <Modal
      size="sm"
      title={
        <p className="font-gilroy font-semibold text-lg">
          {t("Manage Team Seats")}
        </p>
      }
      subtitle={t("Add or remove seats for your team. Changes are billed immediately, prorated for the current billing period.")}
      secondaryButtonProps={{
        text: t("Cancel"),
        onClick: closeModal,
      }}
      primaryButtonProps={{
        text: isSubmitting
          ? t("Updating...")
          : (isIncrease ? t("Add seats & charge today") : isDecrease ? t("Reduce seats") : t("No change")),
        onClick: handleConfirm,
        disabled: isNoChange || isSubmitting || isPreviewLoading || !!previewError,
      }}
    >
      <div className="flex flex-col gap-4 mt-2">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-gray-400 uppercase">{t("Seats")}</span>
            <span className="text-3xl font-gilroy font-bold">{targetSeats}</span>
            <span className="text-xs font-semibold text-gray-400">
              {t("Currently")}: {currentSeats}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={decrement}
              className="btn btn-white p-2 disabled:opacity-50"
              disabled={targetSeats <= minSeats || isSubmitting}
              aria-label={t("Decrease seats")}
            >
              <MinusIcon className="w-5 h-5" />
            </button>
            <input
              type="number"
              className="input w-20 text-center"
              value={targetSeats}
              min={minSeats}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (Number.isNaN(value)) return;
                changeSeats(Math.max(minSeats, value));
              }}
            />
            <button
              type="button"
              onClick={increment}
              className="btn btn-white p-2"
              disabled={isSubmitting}
              aria-label={t("Increase seats")}
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {memberCount > 0 && (
          <p className="text-xs font-semibold text-gray-400">
            {t("Your team currently has")} <span className="text-gray-600">{memberCount}</span> {t("member(s). Seats cannot be reduced below this number.")}
          </p>
        )}

        <div className="border-t pt-4 h-14">
          {isPreviewLoading && (
            <p className="text-sm font-semibold text-gray-400">{t("Calculating price...")}</p>
          )}

          {!isPreviewLoading && previewError && (
            <p className="text-sm font-semibold text-red">{previewError}</p>
          )}

          {!isPreviewLoading && !previewError && isNoChange && (
            <p className="text-sm font-semibold text-gray-400">{t("No changes to apply.")}</p>
          )}

          {!isPreviewLoading && !previewError && !isNoChange && preview && (
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-gray-400 uppercase">
                  {preview.amountDue >= 0 ? t("Charged today") : t("Credit applied")}
                </span>
                <span className="text-xs font-semibold text-gray-400">
                  {t("Prorated for the current billing period")}
                </span>
              </div>
              <span className="font-gilroy font-semibold text-2xl">
                {formatAmount(Math.abs(preview.amountDue), preview.currency)}
              </span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default SettingsTeamSeatsModal;
